import { MatterSubItemContextMenu } from "@/components/features/matter/context-menus/MatterSubItemContextMenu";
import type { SettlementViewModel } from "@/hooks/matter-view-models/use-settlements-view-model";
import { useDeleteMatterSettlementCounter } from "@/lib/data/react-query/matters/matter-settlement-counter";
import { useDeleteMatterSettlementDemand } from "@/lib/data/react-query/matters/matter-settlement-demand";
import { useDeleteMatterSettlementOffer } from "@/lib/data/react-query/matters/matter-settlement-offer";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { useUser } from "@/components/UserProvider";
import type { ReactNode } from "react";

interface MatterSettlementNegotiationContextMenuProps {
  matter: Matter;
  negotiation: SettlementViewModel;
  onEdit: () => void;
  trigger: ReactNode;
}

export const MatterSettlementNegotiationContextMenu = ({
  matter,
  negotiation,
  onEdit,
  trigger,
}: MatterSettlementNegotiationContextMenuProps) => {
  const { hasActionPermission } = useUser();
  const deleteDemand = useDeleteMatterSettlementDemand();
  const deleteOffer = useDeleteMatterSettlementOffer();
  const deleteCounter = useDeleteMatterSettlementCounter();
  const permissionKey =
    negotiation.type === "Demand"
      ? ACTION_PERMISSION_KEYS.matterSettlementDemand
      : negotiation.type === "Offer"
        ? ACTION_PERMISSION_KEYS.matterSettlementOffer
        : ACTION_PERMISSION_KEYS.matterSettlementCounter;
  const canDelete = hasActionPermission(
    permissionKey,
    ACTION_PERMISSION_VALUES.delete,
  );
  const date =
    negotiation.type === "Offer"
      ? negotiation.dateReceived
      : negotiation.dateSent;
  const status =
    negotiation.type === "Offer" || negotiation.type === "Counter"
      ? negotiation.status
      : undefined;
  const isDeleting =
    negotiation.type === "Demand"
      ? deleteDemand.isPending
      : negotiation.type === "Offer"
        ? deleteOffer.isPending
        : deleteCounter.isPending;
  const subItemType =
    negotiation.type === "Demand"
      ? "settlement-demand"
      : negotiation.type === "Offer"
        ? "settlement-offer"
        : "settlement-counter";

  const handleDelete = async () => {
    const data = { matterId: matter.id, id: negotiation.id };
    if (negotiation.type === "Demand") {
      await deleteDemand.mutateAsync(data);
      return;
    }
    if (negotiation.type === "Offer") {
      await deleteOffer.mutateAsync(data);
      return;
    }
    await deleteCounter.mutateAsync(data);
  };

  return (
    <MatterSubItemContextMenu
      createSignatureRequest
      copyValue={[
        negotiation.type,
        negotiation.amount != null
          ? `Amount: $${negotiation.amount}`
          : undefined,
        date ? `Date: ${date}` : undefined,
        status ? `Status: ${status}` : undefined,
      ]
        .filter(Boolean)
        .join("\n")}
      delete={
        canDelete ? { disabled: isDeleting, onSelect: handleDelete } : false
      }
      entityLabel={negotiation.type.toLowerCase()}
      entityName={`settlement ${negotiation.type.toLowerCase()}`}
      matter={matter}
      onEdit={onEdit}
      subItem={{ id: negotiation.id, type: subItemType }}
      trigger={trigger}
    />
  );
};
